import type { InstagramAccount } from '../../types';

import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Box, Flex, Image, Spinner, Text } from '@valwin/facto/components';
import {
  HeartIcon,
  MessageCircleIcon,
  SendIcon,
  UserIcon,
} from '@valwin/facto/icons';

import { PhonePreview } from '~features/integrations/ui';

import { useInstagramShareForm } from '../context';

type InstagramSharePreviewProps = {
  account: InstagramAccount | null | undefined;
  isImageProcessing?: boolean;
};

export function InstagramSharePreview({
  account,
  isImageProcessing = false,
}: InstagramSharePreviewProps) {
  const { control, setValue } = useInstagramShareForm();

  const caption = useWatch({ control, name: 'caption' });
  const imageUrls = useWatch({ control, name: 'imageUrls' });

  const accountName = account?.accountName ?? '';
  const profilePictureUrl = account?.accountProfilePictureUrl;

  // Instagram CDN urls are signed and expire, so a stored one eventually 403s.
  const [failedProfilePictureUrl, setFailedProfilePictureUrl] = useState<
    string | null
  >(null);

  function renderAvatar() {
    if (profilePictureUrl && failedProfilePictureUrl !== profilePictureUrl) {
      return (
        <Image
          src={profilePictureUrl}
          alt={accountName}
          onError={() => setFailedProfilePictureUrl(profilePictureUrl)}
          width="full"
          height="full"
          objectFit="cover"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="full"
        />
      );
    }

    if (accountName) {
      return accountName[0].toUpperCase();
    }

    return <UserIcon size="md" color="pink.700" />;
  }

  return (
    <PhonePreview>
      <Flex direction="column" gap="2" paddingTop="1" paddingBottom="6">
        <Flex align="center" gap="2" paddingX="2">
          <Flex
            alignItems="center"
            justifyContent="center"
            width="8"
            height="8"
            borderRadius="full"
            overflow="hidden"
            background="pink.100"
            color="pink.700"
          >
            {renderAvatar()}
          </Flex>
          <Text textStyle="xs" fontWeight="bold" truncate>
            {accountName}
          </Text>
        </Flex>

        <Box
          position="relative"
          width="full"
          aspectRatio={imageUrls?.[0] ? undefined : '1/1'}
          background={imageUrls?.[0] ? 'transparent' : 'gray.100'}
          overflow="hidden"
        >
          {imageUrls?.[0] && (
            <Image
              src={imageUrls[0]}
              alt="Preview"
              width="full"
              objectFit="cover"
              onError={() => setValue('imageUrls', [])}
            />
          )}
          {isImageProcessing && (
            <Flex
              position="absolute"
              inset="0"
              align="center"
              justify="center"
              background="blackAlpha.400"
            >
              <Spinner color="white" />
            </Flex>
          )}
        </Box>

        <Flex gap="3" paddingX="3" textStyle="xs" color="gray.700">
          <HeartIcon size="md" />
          <MessageCircleIcon size="md" />
          <SendIcon size="md" />
        </Flex>

        {caption && (
          <Box paddingX="3">
            <Text textStyle="xs" whiteSpace="pre-wrap">
              <Box as="span" fontWeight="bold" marginRight="1">
                {accountName}
              </Box>
              {caption}
            </Text>
          </Box>
        )}
      </Flex>
    </PhonePreview>
  );
}
